// Embed: the code for the website, a shareable link, an iframe, and key rotation.

import React, { useState } from 'react';
import { I } from '../../../common/v5/config';
import { Section, Spinner } from './ui';

function copyText(value) {
	if (navigator.clipboard && window.isSecureContext) {
		return navigator.clipboard.writeText(value);
	}
	// Older browsers and plain http: a hidden textarea.
	return new Promise((resolve, reject) => {
		const ta = document.createElement("textarea");
		ta.value = value;
		ta.setAttribute("readonly", "");
		ta.style.position = "fixed";
		ta.style.opacity = "0";
		document.body.appendChild(ta);
		ta.select();
		let ok = false;
		try {
			ok = document.execCommand("copy");
		} catch (e) {
			ok = false;
		}
		document.body.removeChild(ta);
		if (ok) {
			resolve();
		} else {
			reject(new Error("copy failed"));
		}
	});
}

function CopyBlock({ label, value, hint }) {
	const [copied, setCopied] = useState(false);
	const copy = () => {
		copyText(value).then(() => {
			setCopied(true);
			setTimeout(() => setCopied(false), 1500);
		}).catch(() => setCopied(false));
	};
	return (
		<div>
			<div className="aif-copyhead">
				<label className="aif-lbl" style={{ margin: 0 }}>{label}</label>
				<button type="button" className="aif-link" onClick={copy}>
					<i className={"fa " + (copied ? "fa-check" : "fa-clipboard")} aria-hidden="true" /> {copied ? I("Copied") : I("Copy")}
				</button>
			</div>
			<pre className="aif-code">{value}</pre>
			{hint && <div className="aif-hint">{hint}</div>}
		</div>
	);
}

export default function EmbedTab({ form, embed, embedError, busy, onRotateKey }) {
	const live = form.status === "live";
	return (
		<div className="aif-stack">
			{!live && (
				<p className="aif-note">
					{I("The form isn't published yet. You can paste the code now; customers see the form once you publish it.")}
				</p>
			)}
			<Section title={I("Add it to your website")}>
				{embed ? (
					<React.Fragment>
						<CopyBlock label={I("In the page (recommended)")} value={embed.script}
							hint={I("Paste where the form should appear. It takes your site's width and follows its language.")} />
						<CopyBlock label={I("Popup from a floating button")} value={embed.button}
							hint={I("Adds a button in the bottom corner, in your brand colour, that opens the form in a popup. Change data-label for other text; add data-position=\"left\" for the left corner.")} />
						<CopyBlock label={I("Popup from your own button")} value={embed.own_button}
							hint={I("Add data-cention-form-open to any button or link on your site and it opens the form in a popup. Include the script once per page.")} />
						<div>
							<CopyBlock label={I("Link")} value={embed.link}
								hint={I("A page with just the form, for emails, chat replies or a “Contact us” button.")} />
							{live && (
								<a href={embed.link} target="_blank" rel="noopener noreferrer" className="aif-link">
									{I("Open the form")} <i className="fa fa-external-link" aria-hidden="true" />
								</a>
							)}
						</div>
						<CopyBlock label={I("Iframe")} value={embed.iframe} hint={I("For site builders that only accept an iframe.")} />
						<p className="aif-muted aif-small">
							{I("Preselect a case type by adding")} <code>?type=&lt;key&gt;</code> {I("to the link, or")} <code>data-case-type="&lt;key&gt;"</code> {I("to the script's div.")}
						</p>
					</React.Fragment>
				) : embedError ? (
					<div className="aif-error">{I("The embed codes couldn't be loaded:")} {embedError}</div>
				) : (
					<Spinner />
				)}
			</Section>

			<Section title={I("Form key")} hint={I("The key in the code above. A new key stops the old code and link from working.")}>
				<div className="aif-row" style={{ gap: 12 }}>
					<code className="aif-mono">{form.public_key}</code>
					<button type="button" className="aif-btn" disabled={busy}
						onClick={() => {
							if (window.confirm(I("Make a new key? Every website and link using the current code stops showing the form until you update it."))) {
								onRotateKey();
							}
						}}>
						<i className="fa fa-key" aria-hidden="true" /> {I("New key")}
					</button>
				</div>
			</Section>
		</div>
	);
}
